import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, Stethoscope, BookOpen, Pill, Calculator, ArrowRight, ShieldCheck } from "lucide-react";
import analytics, { planToSubscriptionType } from "../lib/analytics";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh, user } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | failed | pending
  const reference = params.get("reference") || params.get("m_payment_id");
  const conversionFired = useRef(false);
  const failureFired = useRef(false);

  // Fire the GA4 `purchase` conversion ONLY on a genuine, confirmed successful
  // payment. Delivered via direct gtag() and de-duplicated per transaction_id
  // (in analytics.purchase) so duplicate PayFast callbacks / reloads can never
  // create a second purchase event.
  useEffect(() => {
    if (status !== "success" || conversionFired.current) return;
    conversionFired.current = true;
    const planValues = { "plan-monthly": 89, "plan-annual": 899 };
    const plan = user?.subscription_plan;
    analytics.purchase(
      reference || undefined,
      planValues[plan] ?? undefined,
      planToSubscriptionType(plan),
    );
  }, [status, user, reference]);

  // Diagnostic-only payment_failed event (NOT a conversion).
  useEffect(() => {
    if (status !== "failed" || failureFired.current) return;
    failureFired.current = true;
    analytics.error("payment_failed", "payfast_declined_or_error", "PaymentCallback");
  }, [status]);

  useEffect(() => {
    let tries = 0;
    let stopped = false;

    // Direct-to-Payfast "Pay Now" buttons don't carry a reference. In that case
    // we poll the account itself and wait for the ITN webhook to activate it.
    const pollAccount = async () => {
      tries += 1;
      try {
        const u = await refresh();
        if (u && (u.subscription_status === "active" || u.subscription_status === "trial")) {
          if (!stopped) setStatus("success");
          return;
        }
      } catch (e) {
        // Expected while the ITN webhook is still in flight (401/stale account);
        // log for debugging but keep polling until we hit the retry cap.
        console.debug("payment poll: account not active yet, retrying", e?.response?.status || e?.message);
      }
      if (tries < 20 && !stopped) {
        setTimeout(pollAccount, 2000);
      } else if (!stopped) {
        setStatus("pending");
      }
    };

    // In-app checkout carries a reference we can poll directly.
    const pollReference = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/subscription/status/${reference}`);
        if (data.status === "success") {
          await refresh();
          if (!stopped) setStatus("success");
          return;
        }
        if (data.status === "failed") {
          if (!stopped) setStatus("failed");
          return;
        }
      } catch (e) {
        if (!stopped) setStatus("failed");
        return;
      }
      if (tries < 15 && !stopped) {
        setTimeout(pollReference, 2000);
      } else if (!stopped) {
        setStatus("pending");
      }
    };

    if (reference) {
      pollReference();
    } else {
      pollAccount();
    }
    return () => { stopped = true; };
  }, [reference, refresh]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="payment-callback-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-[#111827]" />
          <span className="font-heading font-bold text-[#111827]">RapidCare SA</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {status === "verifying" && <VerifyingCard />}
        {status === "success" && <SuccessCard userName={user?.full_name} planName={user?.subscription_plan} onDashboard={() => nav("/app/dashboard")} />}
        {status === "pending" && <PendingCard onDashboard={() => nav("/app/dashboard")} />}
        {status === "failed" && <FailedCard />}
      </main>
    </div>
  );
}

function VerifyingCard() {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="payment-verifying-card">
      <CardContent className="p-10 text-center">
        <Loader2 className="w-12 h-12 text-[#111827] animate-spin mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-extrabold text-[#111827]">Verifying your payment…</h1>
        <p className="text-sm text-slate-600 mt-3 max-w-md mx-auto">
          We are confirming your subscription with Payfast. This usually takes only a few seconds — please don&apos;t refresh or close this page.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" />
          Encrypted via Payfast • PCI-DSS compliant
        </div>
      </CardContent>
    </Card>
  );
}

function SuccessCard({ userName, planName, onDashboard }) {
  const planLabel = planName === "plan-annual" ? "Premium Annual" : planName === "plan-monthly" ? "Premium Monthly" : "Premium";
  const firstName = (userName || "").trim().split(" ")[0];
  return (
    <Card className="border-emerald-200 shadow-sm overflow-hidden" data-testid="payment-success-card">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-8 py-10 text-center border-b border-emerald-100">
        <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#111827]">
          Welcome to {planLabel}{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-slate-600 mt-3 max-w-lg mx-auto">
          Your payment was successful and your access is now active. A receipt has been emailed to you by Payfast.
        </p>
      </div>

      <CardContent className="p-8">
        <p className="font-heading font-semibold text-sm uppercase tracking-wide text-slate-500 mb-4">
          You now have full access to
        </p>
        <ul className="grid sm:grid-cols-2 gap-3">
          <NextStep icon={<BookOpen className="w-4 h-4" />} title="All 500+ clinical protocols" desc="Emergency, paediatric, surgical and more — all SA-guideline-aligned." />
          <NextStep icon={<Pill className="w-4 h-4" />} title="Full drug database + interactions" desc="Adult & paediatric dosing, antibiotic guide, ICD-10 search." />
          <NextStep icon={<Calculator className="w-4 h-4" />} title="30+ medical calculators" desc="CURB-65, HEART, CIWA, MEWS, MELD-Na, paed fluids and more." />
          <NextStep icon={<ShieldCheck className="w-4 h-4" />} title="No auto-billing" desc="Once-off payment for the period. You're never charged automatically — renew anytime from Settings." />
        </ul>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button onClick={onDashboard} className="bg-[#0B5FFF] hover:bg-[#0A54E0] gap-2" data-testid="go-dashboard-after-pay">
            Open dashboard <ArrowRight className="w-4 h-4" />
          </Button>
          <Link to="/app/protocols"><Button variant="outline" className="w-full sm:w-auto">Browse protocols</Button></Link>
          <Link to="/app/calculators"><Button variant="outline" className="w-full sm:w-auto">Open calculators</Button></Link>
        </div>
        <p className="text-xs text-slate-500 mt-6">
          Reference only. Requires a qualified medical practitioner to approve any treatments.
        </p>
      </CardContent>
    </Card>
  );
}

function PendingCard({ onDashboard }) {
  return (
    <Card className="border-amber-200 shadow-sm" data-testid="payment-pending-card">
      <CardContent className="p-10 text-center">
        <Loader2 className="w-14 h-14 text-amber-600 animate-spin mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-extrabold text-[#111827]">Awaiting Payfast confirmation</h1>
        <p className="text-sm text-slate-600 mt-3 max-w-lg mx-auto">
          Your payment was submitted but Payfast hasn&apos;t notified us yet. This can take up to a minute. You can safely close this page —
          your subscription will activate automatically once the confirmation arrives. If it doesn&apos;t, our team will reconcile it within 24 hours.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
          <Button onClick={onDashboard} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid="go-dashboard-pending">
            Go to dashboard
          </Button>
          <a href="mailto:rapidcaresasupport@gmail.com"><Button variant="outline">Email support</Button></a>
        </div>
      </CardContent>
    </Card>
  );
}

function FailedCard() {
  return (
    <Card className="border-rose-200 shadow-sm" data-testid="payment-failed-card">
      <CardContent className="p-10 text-center">
        <XCircle className="w-14 h-14 text-rose-600 mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-extrabold text-[#111827]">Payment could not be verified</h1>
        <p className="text-sm text-slate-600 mt-3 max-w-lg mx-auto">
          Either the payment failed at Payfast, or we couldn&apos;t match it to your account. No charge has been made to your card.
          Please try again, or contact support if your card was charged.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
          <Link to="/pricing"><Button className="bg-[#0B5FFF] hover:bg-[#0A54E0]">Try again</Button></Link>
          <Link to="/app/dashboard"><Button variant="outline">Back to app</Button></Link>
          <a href="mailto:rapidcaresasupport@gmail.com"><Button variant="ghost">Email support</Button></a>
        </div>
      </CardContent>
    </Card>
  );
}

function NextStep({ icon, title, desc }) {
  return (
    <li className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <div className="w-7 h-7 rounded-md bg-[#F1F5F9] text-[#111827] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-heading font-semibold text-sm text-[#111827]">{title}</div>
        <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
      </div>
    </li>
  );
}
